import { definedMap } from "@glideapps/ts-necessities";
import type {
    AppDescriptionContext,
    ExistingAppDescriptionContext,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
} from ".";
import type { AppDescription, AppKind, UserFeatures } from "../../common/components/SerializedApp";
import { getAppKind, getSourceMetadata } from "../../common/components/SerializedApp";
import type { UserProfileTableInfo } from "../../common/components/types";
import type { BuilderActionsForApp } from "../../common/Database/builder-actions";
import type { EminenceFlags } from "../../common/Database/eminence";
import type { TypeSchema } from "../../common/description";
import { findTable } from "../../common/description";
import { memoizeFunction } from "../../common/memoize";
import type { SourceMetadata } from "../../common/source-metadata";
import { getUserProfileTableInfo } from "../user-profile-info";

export function makeSimpleSchemaInspector(
    schema: TypeSchema,
    sourceMetadata: readonly SourceMetadata[] | undefined,
    userProfileTableInfo: UserProfileTableInfo | undefined
): SchemaInspector {
    return {
        schema,
        userProfileTableInfo,
        sourceMetadata,
        findTable: r => findTable(schema, r, sourceMetadata),
    };
}

function makeSimpleAppDescriptionContextUnmemoized(
    appID: string,
    appKind: AppKind,
    appDescription: AppDescription,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): ExistingAppDescriptionContext;
function makeSimpleAppDescriptionContextUnmemoized(
    appID: string,
    appKind: AppKind,
    appDescription: AppDescription | undefined,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): AppDescriptionContext;
function makeSimpleAppDescriptionContextUnmemoized(
    appID: string,
    appKind: AppKind,
    appDescription: AppDescription | undefined,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): AppDescriptionContext {
    return {
        ...makeSimpleSchemaInspector(
            schema,
            definedMap(appDescription, getSourceMetadata),
            definedMap(appDescription, getUserProfileTableInfo)
        ),
        appID,
        appKind,
        appDescription,
        builderActions: builderActions ?? new Map(),
        userFeatures,
        eminenceFlags,
        getBuilderAction: id => builderActions?.get(id),
    };
}

export const makeSimpleAppDescriptionContext = memoizeFunction(
    "makeSimpleAppDescriptionContext",
    makeSimpleAppDescriptionContextUnmemoized
);

export function elevateAppDescriptionContext<T extends AppDescriptionContext>(
    adc: T
): (T & ExistingAppDescriptionContext) | undefined {
    if (adc.appDescription === undefined) return undefined;
    return adc as T & ExistingAppDescriptionContext;
}

export function makeSimpleComponentConfiguratorContext(
    appID: string,
    appDescription: AppDescription,
    builderActions: BuilderActionsForApp,
    schema: TypeSchema,
    userFeatures: UserFeatures,
    eminenceFlags: EminenceFlags
): RewritingComponentConfiguratorContext {
    return {
        ...makeSimpleAppDescriptionContext(
            appID,
            getAppKind(appDescription),
            appDescription,
            builderActions,
            schema,
            userFeatures,
            eminenceFlags
        ),
        appKind: getAppKind(appDescription),
        getBuilderAction: id => builderActions.get(id),
        requireScreen: n => appDescription.screenDescriptions[n] !== undefined,
        requireMenuScreen: () => true,
    };
}
